// ============================================================
// MOONSHINE MASTERS — moonshineOnline.js        THE MARK + BREWING TOGETHER
//
// caP'n, 2026-10-24: "then we add co-op via first adding The Mark proper".
// Borrowed from Hooked (hookedOnline.js, the walkable-world template - the borrow-first rule):
//   · ONE SOUL: ieIdentity (vendored byte-identical from gamemaster/engine). Signed OUT, Moonshine is the
//     same solo game it always was; signed IN, you carry your Mark here like everywhere else.
//   · WHICH SERVER: ieServers (vendored). A Moonshine server holds 4 (SERVER_CAPS.moonshine - co-op up to 4).
//   · THE FRIENDS PANEL + THE MARK: IEFriends / Mark / MarkEditor (vendored). Nothing here re-implements them.
// This file is only Moonshine's own part: the sign-in card (in Moonshine's colours), and - next - what a
// Moonshine server CONTAINS (the other brewers, the shared yard). Every path derives from ONE base (the
// Firebase Law #2): games/moonshine/...
// ============================================================
import { useState, useEffect } from "react";
import { C } from "./theme";

export const MOONSHINE_BASE = "games/moonshine";            // ⚑ the ONE drawer (games/{game_id}); every path below derives
export const GAME_ID = "moonshine";
// the server chip / party prompt in Moonshine's colours (ieServers reads these names)
// THE KRAFT LABEL (2026-10-31): the server kit (ieServers.js, byte-identical - never edited) takes HEX only (it adds alpha to them):
// kraft -> cream paper, ink text, a red stamp for the big button (its text = bg, cream on red).
export const MOONSHINE_SERVER_THEME = { accent: C.red, ink: C.ink, dim: C.dim, bg: "#EADBB8", panel: "#C9A06A", font: C.font, display: C.display, z: 900 };

// a key pressed while typing a name or a password - or while a sign-in / Mark / server card is up - must never
// walk, swap a jar or open the satchel. App.js sets ONLINE_MODAL.open; every game key handler asks typing(e).
export const ONLINE_MODAL = { open: false };
// A Mark opened from the FRIENDS PANEL isn't App's (the dock is App's sibling), so look for it on the page:
// every Mark / MarkEditor card draws a .mk-tab or .mk-scroll (Mark.js). Moonshine-only; the shared Mark is untouched.
const markUp = () => { try { return !!document.querySelector(".mk-tab, .mk-scroll"); } catch (err) { return false; } };
export const typing = (e) => {
  if (ONLINE_MODAL.open || markUp()) return true;
  const t = e && e.target; return !!t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable);
};

// ── SIGNING IN (so you can brew with friends) ── Moonshine never needs an account to play solo:
// this card only opens when you ask for company. Same soul as the Pier and Terror.
export function SignInCard({ auth, onClose }) {
  const [mode, setMode] = useState(auth.phase === "name" ? "name" : "in");
  const [email, setEmail] = useState(auth.guestEmail || "");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState(null);
  // after a good answer, the soul's phase decides: named -> done; signed in but nameless -> ask the name right here
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (!waiting) return;
    if (auth.phase === "ready") onClose();
    else if (auth.phase === "name") { setMode("name"); setWaiting(false); }
  }, [waiting, auth.phase, onClose]);
  const go = async (e) => {
    e.preventDefault(); setMsg(null);
    const r = mode === "name" ? await auth.claimName(name)
      : mode === "up" ? await auth.signUp(email, pw, name)
      : await auth.signIn(email, pw);
    if (r && r.ok) setWaiting(true); else setMsg((r && r.error) || auth.error || "That didn't work.");
  };
  const field = {
    width: "100%", boxSizing: "border-box", marginTop: 8, padding: "10px 12px", borderRadius: 3,
    background: C.cream, color: C.ink, border: `1px solid ${C.edge}`, fontFamily: C.body, fontSize: 15, outline: "none",
  };
  const stop = (e) => e.stopPropagation();
  return (
    <div onKeyDown={(e) => { stop(e); if (e.key === "Escape") onClose(); }} onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 880, display: "grid", placeItems: "center", background: "rgba(0,0,0,0.55)" }}>
      <form onSubmit={go} style={{
        width: "min(360px, calc(100vw - 32px))", padding: "26px 24px 18px", borderRadius: 3,
        background: C.panel, border: `1px solid ${C.edge}`, boxShadow: "0 18px 60px rgba(0,0,0,0.75)", fontFamily: C.body, color: C.ink,
      }}>
        <div style={{ fontFamily: C.display, fontSize: 28, lineHeight: 1, letterSpacing: 0.5, color: C.ink, textAlign: "center" }}>
          {mode === "name" ? "WHAT DO WE CALL YOU?" : "BREW WITH FRIENDS"}
        </div>
        <div style={{ fontSize: 14, color: C.dim, textAlign: "center", marginTop: 4 }}>
          {mode === "name" ? "One name, every game." : "Same you as the Pier and Terror."}
        </div>
        {mode !== "name" && <input style={field} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />}
        {mode !== "name" && <input style={field} type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} />}
        {mode !== "in" && <input style={field} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={24} autoFocus={mode === "name"} />}
        {msg && <div style={{ color: C.bad, fontSize: 13, marginTop: 8, textAlign: "center" }}>{msg}</div>}
        <button type="submit" disabled={auth.busy} style={{
          width: "100%", marginTop: 14, padding: "11px 0", cursor: "pointer",
          fontFamily: C.font, fontSize: 13, letterSpacing: 2, color: "#FFF1E6", background: C.red, border: "none", borderRadius: 3,
        }}>{mode === "name" ? "THAT'S ME" : mode === "up" ? "MAKE ONE" : "SIGN IN"}</button>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 13 }}>
          {mode !== "name"
            ? <span onClick={() => setMode(mode === "in" ? "up" : "in")} style={{ color: C.amberHi, cursor: "pointer", textDecoration: "underline" }}>{mode === "in" ? "New here? Make one" : "Have one? Sign in"}</span>
            : <span />}
          <span onClick={onClose} style={{ color: C.dim, cursor: "pointer" }}>Brew alone</span>
        </div>
      </form>
    </div>
  );
}
